<template>
  <div class="knowledge-agent bg-neutral-50 min-h-screen">
    <main class="container mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
      <!-- Hero -->
      <section class="text-center mb-8">
        <div
          class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-600 text-xs font-medium mb-4"
        >
          <i class="fa fa-book"></i>
          教师自己的知识库与智能体
        </div>
        <h1 class="text-3xl md:text-5xl font-bold text-neutral-900 mb-4">
          知识智能体
        </h1>
        <p class="text-base md:text-lg text-neutral-500 max-w-2xl mx-auto">
          上传教案、资料或收藏的链接，选一个你熟悉的模型，它就能基于你的资料回答问题。
          也可以直接嵌入你已经做好的外部智能体。
        </p>
      </section>

      <!-- 切换 + 新建 -->
      <section class="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div class="flex gap-2">
          <button
            v-for="t in tabs"
            :key="t.id"
            @click="switchTab(t.id)"
            class="px-4 py-2 rounded-full text-sm transition-colors"
            :class="
              tab === t.id
                ? 'bg-primary text-white font-medium'
                : 'bg-white text-neutral-600 border border-neutral-200 hover:border-primary hover:text-primary'
            "
          >
            {{ t.label }}
            <span v-if="t.id === 'mine' && mineList.length" class="ml-1 text-xs opacity-70"
              >{{ mineList.length }}</span
            >
          </button>
        </div>
        <button
          @click="openCreate"
          class="px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:opacity-90 transition-opacity"
        >
          <i class="fa fa-plus mr-1"></i> 创建智能体
        </button>
      </section>

      <!-- 加载 -->
      <div v-if="loading" class="text-center py-16 text-neutral-400">
        <i class="fa fa-circle-o-notch fa-spin text-2xl"></i>
      </div>

      <!-- 空态 -->
      <div
        v-else-if="!currentList.length"
        class="bg-white rounded-xl border border-dashed border-neutral-300 py-16 text-center"
      >
        <i class="fa fa-inbox text-3xl text-neutral-300 mb-3"></i>
        <p class="text-neutral-500 text-sm">
          {{ tab === "mine" ? "你还没有创建智能体" : "暂时还没有公开的智能体" }}
        </p>
        <button
          v-if="tab === 'mine'"
          @click="openCreate"
          class="mt-4 px-4 py-2 rounded-lg bg-primary text-white text-sm"
        >
          创建第一个
        </button>
      </div>

      <!-- 卡片列表 -->
      <div v-else class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <div
          v-for="a in currentList"
          :key="a.id"
          class="bg-white rounded-xl border border-neutral-200 p-5 hover:shadow-md hover:border-primary/40 transition-all flex flex-col"
        >
          <div class="flex items-start gap-3 mb-3">
            <div
              class="w-11 h-11 rounded-xl flex items-center justify-center text-white font-bold flex-shrink-0 overflow-hidden"
              :style="{ background: colorOf(a.id) }"
            >
              <img
                v-if="a.avatar"
                :src="a.avatar"
                class="w-full h-full object-cover"
                alt=""
              />
              <span v-else>{{ (a.name || "智")[0] }}</span>
            </div>
            <div class="flex-1 min-w-0">
              <h3 class="font-bold text-neutral-900 truncate">
                {{ a.name }}
              </h3>
              <div class="flex items-center gap-2 mt-1">
                <span
                  class="text-[11px] px-2 py-0.5 rounded-full"
                  :class="
                    a.mode === 'embed'
                      ? 'bg-sky-50 text-sky-600'
                      : 'bg-emerald-50 text-emerald-600'
                  "
                >
                  <i
                    :class="
                      a.mode === 'embed' ? 'fa fa-code' : 'fa fa-database'
                    "
                    class="mr-1"
                  ></i>
                  {{ a.mode === "embed" ? "外部嵌入" : "自建知识库" }}
                </span>
                <span
                  v-if="a.mine"
                  class="text-[11px]"
                  :class="auditClass(a)"
                >
                  <i :class="['fa', auditIcon(a)]" class="mr-1"></i>{{ auditText(a) }}
                </span>
              </div>
            </div>
          </div>

          <p class="text-xs text-neutral-500 line-clamp-2 mb-3 flex-1 min-h-[2.5rem]">
            {{ a.intro || "暂无简介" }}
          </p>

          <div
            class="flex items-center justify-between text-xs text-neutral-400 mb-3 pb-3 border-b border-neutral-100"
          >
            <span class="truncate">
              <i class="fa fa-user-o mr-1"></i>{{ a.creatorName || "老师" }}
            </span>
            <span class="flex-shrink-0">
              <template v-if="a.mode === 'kb'">
                <i class="fa fa-file-text-o mr-1"></i>{{ a.docCount || 0 }} 篇
                <i class="fa fa-link ml-2 mr-1"></i>{{ a.linkCount || 0 }} 链接
              </template>
              <template v-else>嵌入页面</template>
            </span>
          </div>

          <div class="flex items-center gap-2 flex-wrap">
            <button
              @click="openAgent(a)"
              class="flex-1 px-3 py-1.5 rounded-lg bg-primary text-white text-xs font-medium hover:opacity-90"
            >
              {{ a.mode === "embed" ? "打开" : "开始对话" }}
            </button>
            <template v-if="a.mine">
              <button
                v-if="a.mode === 'kb'"
                @click="openResource(a)"
                class="px-3 py-1.5 rounded-lg border border-neutral-200 text-xs text-neutral-600 hover:border-primary hover:text-primary"
                title="资源库"
              >
                <i class="fa fa-folder-open-o"></i>
              </button>
              <button
                @click="openEdit(a)"
                class="px-3 py-1.5 rounded-lg border border-neutral-200 text-xs text-neutral-600 hover:border-primary hover:text-primary"
                title="编辑"
              >
                <i class="fa fa-pencil"></i>
              </button>
              <button
                v-if="a.auditStatus === 1"
                class="px-3 py-1.5 rounded-lg border border-amber-200 text-amber-600 bg-amber-50 text-xs"
                title="已提交，等待管理员审核"
                disabled
              >
                <i class="fa fa-clock-o mr-1"></i>待审核
              </button>
              <button
                v-else-if="a.auditStatus === 2"
                @click="togglePublic(a)"
                class="px-3 py-1.5 rounded-lg border border-amber-200 text-amber-600 bg-amber-50 text-xs"
                title="已发布到广场，点击取消发布"
              >
                <i class="fa fa-globe mr-1"></i>已发布
              </button>
              <button
                v-else
                @click="togglePublic(a)"
                class="px-3 py-1.5 rounded-lg border border-neutral-200 text-neutral-500 text-xs hover:border-primary hover:text-primary"
                title="申请发布到 AI 应用广场（需管理员审核）"
              >
                <i class="fa fa-paper-plane mr-1"></i>
                {{ a.auditStatus === 3 ? "重新提交" : "发布" }}
              </button>
              <button
                @click="removeAgent(a)"
                class="px-3 py-1.5 rounded-lg border border-neutral-200 text-xs text-neutral-400 hover:border-red-300 hover:text-red-500"
                title="删除"
              >
                <i class="fa fa-trash-o"></i>
              </button>
            </template>
          </div>

          <p v-if="a.mine && a.mode === 'kb' && !a.keyReady" class="mt-2 text-[11px] text-amber-500">
            <i class="fa fa-exclamation-circle mr-1"></i>尚未配置模型 API Key，无法对话
          </p>
          <p v-if="a.mine && a.auditStatus === 3 && a.auditRemark" class="mt-2 text-[11px] text-red-500">
            <i class="fa fa-exclamation-circle mr-1"></i>审核未通过：{{ a.auditRemark }}
          </p>
        </div>
      </div>
    </main>

    <!-- ============ 创建 / 编辑 ============ -->
    <div
      v-if="editVisible"
      class="fixed inset-0 z-50 flex items-start justify-center bg-black/40 overflow-y-auto py-8 px-4"
    >
      <div class="bg-white rounded-2xl w-full max-w-2xl p-6 shadow-xl">
        <div class="flex items-center justify-between mb-5">
          <h2 class="text-lg font-bold text-neutral-900">
            {{ form.id ? "编辑智能体" : "创建智能体" }}
          </h2>
          <button class="text-neutral-400 hover:text-neutral-600" @click="editVisible = false">
            <i class="fa fa-times"></i>
          </button>
        </div>

        <!-- 模式选择 -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
          <button
            v-for="m in modes"
            :key="m.id"
            type="button"
            @click="setMode(m.id)"
            class="text-left p-4 rounded-xl border-2 transition-all"
            :class="
              form.mode === m.id
                ? 'border-primary bg-primary/5'
                : 'border-neutral-200 hover:border-neutral-300'
            "
          >
            <div class="flex items-center gap-2 mb-1">
              <i :class="['fa', m.icon]" class="text-primary"></i>
              <span class="font-bold text-sm text-neutral-900">{{ m.label }}</span>
            </div>
            <p class="text-xs text-neutral-500 leading-relaxed">{{ m.desc }}</p>
          </button>
        </div>

        <div class="space-y-4">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label class="block text-xs font-medium text-neutral-600 mb-1">名称 *</label>
              <input
                v-model="form.name"
                maxlength="30"
                placeholder="如：三年级语文备课助手"
                class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-primary"
              />
            </div>
            <div>
              <label class="block text-xs font-medium text-neutral-600 mb-1">头像地址（可选）</label>
              <input
                v-model="form.avatar"
                placeholder="https://..."
                class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-primary"
              />
            </div>
          </div>

          <div>
            <label class="block text-xs font-medium text-neutral-600 mb-1">一句话简介</label>
            <textarea
              v-model="form.intro"
              rows="2"
              maxlength="200"
              placeholder="它能帮你做什么？"
              class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-primary resize-none"
            ></textarea>
          </div>

          <!-- 嵌入模式 -->
          <div v-if="form.mode === 'embed'" class="space-y-4">
            <div>
              <label class="block text-xs font-medium text-neutral-600 mb-1">嵌入地址 *</label>
              <input
                v-model="form.embedUrl"
                placeholder="https://www.coze.cn/store/bot/xxxx 或你自建的页面地址"
                class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-primary"
              />
              <p class="text-[11px] text-neutral-400 mt-1">
                填写可在 iframe 中打开的地址。若对方站点禁止被嵌入，页面可能显示空白。
              </p>
            </div>
          </div>

          <!-- 知识库模式 -->
          <div v-else class="space-y-4">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label class="block text-xs font-medium text-neutral-600 mb-1">模型厂商</label>
                <select
                  v-model="form.provider"
                  @change="onProviderChange"
                  class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-primary bg-white"
                >
                  <option v-for="p in providers" :key="p.id" :value="p.id">
                    {{ p.label }}
                  </option>
                </select>
              </div>
              <div>
                <label class="block text-xs font-medium text-neutral-600 mb-1">模型名 *</label>
                <input
                  v-model="form.model"
                  :placeholder="currentProvider.modelHint"
                  class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-primary"
                />
              </div>
            </div>

            <div>
              <label class="block text-xs font-medium text-neutral-600 mb-1">接口地址</label>
              <input
                v-model="form.apiBase"
                :placeholder="currentProvider.base"
                class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-primary"
              />
              <p class="text-[11px] text-neutral-400 mt-1">
                留空则使用厂商默认地址。需为 OpenAI 兼容接口（以 /v1 结尾）。
              </p>
            </div>

            <div>
              <label class="block text-xs font-medium text-neutral-600 mb-1">
                API Key {{ form.id ? "（留空表示不修改）" : "*" }}
              </label>
              <input
                v-model="form.apiKey"
                type="password"
                autocomplete="new-password"
                :placeholder="form.keyMask ? '已配置：' + form.keyMask : '粘贴你自己的 API Key'"
                class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-primary"
              />
              <p class="text-[11px] text-neutral-400 mt-1">
                <i class="fa fa-lock mr-1"></i>Key 仅保存在本站服务端并加密存储，不会下发给任何访客。
              </p>
            </div>

            <div>
              <label class="block text-xs font-medium text-neutral-600 mb-1">角色设定（可选）</label>
              <textarea
                v-model="form.systemPrompt"
                rows="3"
                placeholder="例如：你是小学语文老师，回答要简洁，面向三年级学生。"
                class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-primary resize-none"
              ></textarea>
            </div>

            <div>
              <label class="block text-xs font-medium text-neutral-600 mb-1">开场白（可选）</label>
              <input
                v-model="form.welcome"
                placeholder="你好，我是你的备课助手，可以问我教材里的任何问题。"
                class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-primary"
              />
            </div>
          </div>
        </div>

        <div class="flex justify-end gap-2 mt-6">
          <button
            class="px-4 py-2 rounded-lg border border-neutral-200 text-sm text-neutral-600"
            @click="editVisible = false"
          >
            取消
          </button>
          <button
            class="px-5 py-2 rounded-lg bg-primary text-white text-sm font-medium disabled:opacity-50"
            :disabled="saving"
            @click="save"
          >
            {{ saving ? "保存中..." : "保存" }}
          </button>
        </div>
      </div>
    </div>

    <!-- ============ 资源库 ============ -->
    <div
      v-if="resVisible"
      class="fixed inset-0 z-50 flex items-start justify-center bg-black/40 overflow-y-auto py-8 px-4"
    >
      <div class="bg-white rounded-2xl w-full max-w-3xl p-6 shadow-xl">
        <div class="flex items-center justify-between mb-2">
          <h2 class="text-lg font-bold text-neutral-900">
            资源库 · {{ resAgent.name }}
          </h2>
          <button class="text-neutral-400 hover:text-neutral-600" @click="resVisible = false">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <p class="text-xs text-neutral-500 mb-5">
          上传的资料会被切成片段，对话时自动检索最相关的内容。支持
          txt / md / csv / json / html，单个不超过 5MB。
        </p>

        <!-- 上传区 -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
          <label
            class="border-2 border-dashed border-neutral-300 rounded-xl p-4 text-center cursor-pointer hover:border-primary transition-colors"
          >
            <input
              ref="fileInput"
              type="file"
              class="hidden"
              accept=".txt,.md,.markdown,.csv,.json,.html,.htm,.log"
              @change="uploadFile"
            />
            <i class="fa fa-cloud-upload text-2xl text-neutral-400 mb-2"></i>
            <p class="text-sm text-neutral-700 font-medium">上传文档</p>
            <p class="text-[11px] text-neutral-400 mt-1">
              {{ uploading ? "正在解析..." : "点击选择文件" }}
            </p>
          </label>

          <div class="border border-neutral-200 rounded-xl p-4">
            <p class="text-sm text-neutral-700 font-medium mb-2">
              <i class="fa fa-link mr-1 text-primary"></i>添加链接
            </p>
            <input
              v-model="linkForm.name"
              placeholder="名称（可选）"
              class="w-full mb-2 px-2 py-1.5 border border-neutral-200 rounded text-xs focus:outline-none focus:border-primary"
            />
            <div class="flex gap-2">
              <input
                v-model="linkForm.url"
                placeholder="https://..."
                class="flex-1 px-2 py-1.5 border border-neutral-200 rounded text-xs focus:outline-none focus:border-primary"
              />
              <button
                class="px-3 py-1.5 rounded bg-primary text-white text-xs"
                @click="addLink"
              >
                添加
              </button>
            </div>
          </div>
        </div>

        <!-- 资源列表 -->
        <div v-if="!resources.length" class="text-center py-8 text-neutral-400 text-sm">
          还没有资源，先上传一份资料试试
        </div>
        <div v-else class="divide-y divide-neutral-100 max-h-80 overflow-y-auto">
          <div
            v-for="r in resources"
            :key="r.id"
            class="flex items-center gap-3 py-2.5"
          >
            <i
              :class="r.type === 'link' ? 'fa fa-link text-sky-500' : 'fa fa-file-text-o text-emerald-500'"
              class="fa w-4 text-center"
            ></i>
            <div class="flex-1 min-w-0">
              <p class="text-sm text-neutral-800 truncate">{{ r.name }}</p>
              <p class="text-[11px] text-neutral-400 truncate">
                <template v-if="r.type === 'link'">{{ r.url }}</template>
                <template v-else>{{ r.size }} 字 · {{ r.chunks }} 段</template>
              </p>
            </div>
            <button
              class="text-neutral-400 hover:text-red-500 px-2"
              @click="removeResource(r)"
              title="删除"
            >
              <i class="fa fa-trash-o"></i>
            </button>
          </div>
        </div>

        <div class="flex justify-end mt-5">
          <button
            class="px-4 py-2 rounded-lg bg-primary text-white text-sm"
            @click="resVisible = false"
          >
            完成
          </button>
        </div>
      </div>
    </div>

    <!-- ============ 嵌入模式 ============ -->
    <div
      v-if="embedVisible"
      class="fixed inset-0 z-50 bg-black/50 flex flex-col"
    >
      <div class="flex items-center justify-between px-4 py-2 bg-white border-b border-neutral-200">
        <span class="font-bold text-sm text-neutral-900">{{ activeAgent.name }}</span>
        <div class="flex items-center gap-3">
          <a
            :href="activeAgent.embedUrl"
            target="_blank"
            rel="noopener"
            class="text-xs text-neutral-500 hover:text-primary"
          >
            <i class="fa fa-external-link mr-1"></i>新窗口打开
          </a>
          <button class="text-neutral-400 hover:text-neutral-700" @click="embedVisible = false">
            <i class="fa fa-times"></i>
          </button>
        </div>
      </div>
      <iframe
        :src="activeAgent.embedUrl"
        class="flex-1 w-full bg-white"
        frameborder="0"
        allow="microphone; clipboard-write"
      ></iframe>
    </div>

    <!-- ============ 对话 ============ -->
    <div
      v-if="chatVisible"
      class="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 px-0 sm:px-4"
      @click.self="chatVisible = false"
    >
      <div
        class="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl shadow-xl flex flex-col h-[85vh] sm:h-[80vh]"
      >
        <div class="flex items-center gap-3 px-5 py-3 border-b border-neutral-100">
          <div
            class="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold flex-shrink-0"
            :style="{ background: colorOf(activeAgent.id) }"
          >
            {{ (activeAgent.name || "智")[0] }}
          </div>
          <div class="flex-1 min-w-0">
            <p class="font-bold text-sm text-neutral-900 truncate">{{ activeAgent.name }}</p>
            <p class="text-[11px] text-neutral-400 truncate">
              {{ activeAgent.model || "未配置模型" }}
            </p>
          </div>
          <button class="text-neutral-400 hover:text-neutral-700" @click="chatVisible = false">
            <i class="fa fa-times"></i>
          </button>
        </div>

        <div ref="msgBox" class="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-neutral-50">
          <div
            v-for="(m, i) in messages"
            :key="i"
            class="flex"
            :class="m.role === 'user' ? 'justify-end' : 'justify-start'"
          >
            <div
              class="max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words"
              :class="
                m.role === 'user'
                  ? 'bg-primary text-white rounded-br-sm'
                  : 'bg-white text-neutral-800 border border-neutral-200 rounded-bl-sm'
              "
            >
              <template v-if="m.content">{{ m.content }}</template>
              <span v-else-if="m.loading" class="text-neutral-400">
                <i class="fa fa-circle-o-notch fa-spin mr-1"></i>思考中...
              </span>
            </div>
          </div>
        </div>

        <div class="px-4 py-3 border-t border-neutral-100">
          <div class="flex items-end gap-2">
            <textarea
              v-model="input"
              rows="1"
              :disabled="sending"
              placeholder="输入你的问题，Enter 发送"
              class="flex-1 px-3 py-2.5 border border-neutral-200 rounded-xl text-sm resize-none focus:outline-none focus:border-primary max-h-32"
              @keydown.enter.exact.prevent="send"
            ></textarea>
            <button
              class="px-4 py-2.5 rounded-xl bg-primary text-white text-sm font-medium disabled:opacity-50 flex-shrink-0"
              :disabled="sending || !input.trim()"
              @click="send"
            >
              <i class="fa fa-paper-plane"></i>
            </button>
          </div>
          <p v-if="guestRemaining >= 0" class="text-[11px] text-neutral-400 mt-1.5">
            游客今天还可体验 {{ guestRemaining }} 次，登录后不限次数
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from "element-plus";
import { requireLoginPage, isLogin } from "@/utils/authGuard";

export default {
  name: "KnowledgeAgentPage",
  data() {
    return {
      tab: "square",
      loading: false,
      saving: false,
      uploading: false,
      sending: false,

      squareList: [],
      mineList: [],

      tabs: [
        { id: "square", label: "广场" },
        { id: "mine", label: "我的" },
      ],
      modes: [
        {
          id: "kb",
          label: "自建知识库",
          icon: "fa-database",
          desc: "上传资料 + 选模型，基于你自己的资料回答问题",
        },
        {
          id: "embed",
          label: "外部嵌入",
          icon: "fa-code",
          desc: "填入已有智能体的页面地址，直接嵌入展示",
        },
      ],
      providers: [
        {
          id: "deepseek",
          label: "DeepSeek",
          base: "https://api.deepseek.com/v1",
          modelHint: "deepseek-chat",
        },
        {
          id: "qwen",
          label: "通义千问",
          base: "https://dashscope.aliyuncs.com/compatible-mode/v1",
          modelHint: "qwen-plus",
        },
        {
          id: "zhipu",
          label: "智谱 GLM",
          base: "https://open.bigmodel.cn/api/paas/v4",
          modelHint: "glm-4-flash",
        },
        {
          id: "moonshot",
          label: "月之暗面 Kimi",
          base: "https://api.moonshot.cn/v1",
          modelHint: "moonshot-v1-8k",
        },
        {
          id: "openai",
          label: "OpenAI",
          base: "https://api.openai.com/v1",
          modelHint: "gpt-4o-mini",
        },
        {
          id: "ollama",
          label: "本地 Ollama",
          base: "http://127.0.0.1:11434/v1",
          modelHint: "qwen2.5:7b",
        },
        {
          id: "custom",
          label: "自定义 / 其他",
          base: "",
          modelHint: "模型名，如 gpt-4o-mini",
        },
      ],

      editVisible: false,
      form: this.emptyForm(),

      resVisible: false,
      resAgent: {},
      resources: [],
      linkForm: { name: "", url: "" },

      embedVisible: false,
      chatVisible: false,
      activeAgent: {},
      messages: [],
      input: "",
      guestRemaining: -1,
    };
  },
  computed: {
    currentList() {
      return this.tab === "mine" ? this.mineList : this.squareList;
    },
    currentProvider() {
      return (
        this.providers.find((p) => p.id === this.form.provider) ||
        this.providers[0]
      );
    },
  },
  /** 兜底：局部渲染出错时不让整页白屏，并给出可读提示 */
  errorCaptured(err) {
    console.error("[KnowledgeAgent] 渲染异常", err);
    ElMessage.error("页面出现异常：" + (err && err.message ? err.message : "未知错误"));
    return false;
  },
  mounted() {
    this.applyQuery();
    this.load();
  },
  watch: {
    "$route.query": function () {
      this.applyQuery();
    },
  },
  methods: {
    /** 支持导航菜单带参进入：?tab=mine / ?create=1 */
    applyQuery() {
      const q = this.$route.query || {};
      if (q.tab === "mine") {
        this.tab = "mine";
        if (isLogin()) this.loadMine();
      } else if (q.tab === "square") {
        this.tab = "square";
      }
      if (q.create === "1") {
        this.$nextTick(() => this.openCreate());
      }
    },

    emptyForm() {
      return {
        id: "",
        name: "",
        intro: "",
        avatar: "",
        mode: "kb",
        embedUrl: "",
        provider: "deepseek",
        apiBase: "",
        apiKey: "",
        model: "",
        systemPrompt: "",
        welcome: "",
        keyMask: "",
      };
    },

    async load() {
      this.loading = true;
      try {
        const res = await this.$axios({ url: "user/agent/list", method: "GET" });
        if (res.code === 200 && res.data) {
          this.squareList = res.data.list || [];
        }
        if (isLogin()) {
          await this.loadMine();
        }
      } catch (e) {
        /* 静默：广场加载失败不影响浏览 */
      }
      this.loading = false;
      this.openFromQuery();
    },

    /** 支持从 AI 应用广场带 ?open=<id> 直接打开某个智能体 */
    openFromQuery() {
      const id = (this.$route.query || {}).open;
      if (!id) return;
      const hit = this.squareList.find((x) => x.id === id);
      if (hit) this.openAgent(hit);
    },

    async loadMine() {
      try {
        const res = await this.$axios({
          url: "user/agent/mine",
          method: "POST",
          data: { data: await this.$aesEncrypt({}) },
        });
        if (res.code === 200 && res.data) {
          this.mineList = res.data.list || [];
        }
      } catch (e) {
        /* ignore */
      }
    },

    switchTab(id) {
      this.tab = id;
      if (id === "mine") {
        if (!requireLoginPage(this, "我的智能体")) {
          this.tab = "square";
          return;
        }
        this.loadMine();
      }
    },

    /** 切换模式（独立方法而非模板内联赋值，便于兜底与调试） */
    setMode(id) {
      if (!id) return;
      this.form.mode = id === "embed" ? "embed" : "kb";
    },

    openCreate() {
      if (!requireLoginPage(this, "创建智能体")) return;
      this.form = this.emptyForm();
      this.editVisible = true;
    },

    openEdit(a) {
      this.form = {
        id: a.id,
        name: a.name || "",
        intro: a.intro || "",
        avatar: a.avatar || "",
        mode: a.mode || "kb",
        embedUrl: a.embedUrl || "",
        provider: a.provider || "deepseek",
        apiBase: a.apiBase || "",
        apiKey: "",
        model: a.model || "",
        systemPrompt: a.systemPrompt || "",
        welcome: a.welcome || "",
        keyMask: a.keyMask || "",
      };
      this.editVisible = true;
    },

    onProviderChange() {
      const p = this.currentProvider;
      if (p && p.base) {
        this.form.apiBase = p.base;
      }
      if (p && p.modelHint && !this.form.model) {
        this.form.model = p.modelHint;
      }
    },

    async save() {
      if (!this.form.name.trim()) {
        ElMessage.warning("请填写名称");
        return;
      }
      if (this.form.mode === "embed") {
        if (!this.form.embedUrl.trim()) {
          ElMessage.warning("请填写嵌入地址");
          return;
        }
      } else {
        if (!this.form.model.trim()) {
          ElMessage.warning("请填写模型名");
          return;
        }
        if (!this.form.id && !this.form.apiKey.trim()) {
          ElMessage.warning("请填写 API Key");
          return;
        }
      }

      this.saving = true;
      try {
        const payload = {
          id: this.form.id || null,
          name: this.form.name,
          intro: this.form.intro,
          avatar: this.form.avatar,
          mode: this.form.mode,
          embedUrl: this.form.embedUrl,
          provider: this.form.provider,
          apiBase: this.form.apiBase,
          apiKey: this.form.apiKey,
          model: this.form.model,
          systemPrompt: this.form.systemPrompt,
          welcome: this.form.welcome,
        };
        const res = await this.$axios({
          url: "user/agent/save",
          method: "POST",
          data: { data: await this.$aesEncrypt(payload) },
        });
        if (res.code === 200) {
          const pending = res.data && res.data.auditStatus === 1;
          ElMessage.success(pending ? "已保存，修改后需重新审核" : "已保存");
          this.editVisible = false;
          await this.loadMine();
          await this.load();
        } else {
          ElMessage.error(res.msg || "保存失败");
        }
      } catch (e) {
        ElMessage.error("保存失败，请稍后重试");
      }
      this.saving = false;
    },

    /** 发布状态文案 */
    auditText(a) {
      if (!a) return "";
      if (a.auditStatus === 1) return "待审核";
      if (a.auditStatus === 2) return "已发布";
      if (a.auditStatus === 3) return "已驳回";
      return "未发布";
    },
    auditIcon(a) {
      if (!a) return "fa-circle-o";
      if (a.auditStatus === 1) return "fa-clock-o";
      if (a.auditStatus === 2) return "fa-globe";
      if (a.auditStatus === 3) return "fa-times-circle";
      return "fa-circle-o";
    },
    auditClass(a) {
      if (!a) return "text-neutral-400";
      if (a.auditStatus === 1) return "text-amber-500";
      if (a.auditStatus === 2) return "text-emerald-600";
      if (a.auditStatus === 3) return "text-red-500";
      return "text-neutral-400";
    },

    /** 申请发布（提交审核）/ 取消发布 */
    async togglePublic(a) {
      const published = a.auditStatus === 2 || a.isPublic;
      const open = !published;
      try {
        await ElMessageBox.confirm(
          open
            ? "提交后需要管理员审核，通过后才会在 AI 应用广场展示和访问。审核期间该智能体不会公开。"
            : "取消发布后，广场将不再展示它，确定取消？",
          open ? "申请发布" : "取消发布",
          {
            confirmButtonText: open ? "提交审核" : "取消发布",
            cancelButtonText: "返回",
          }
        );
      } catch (e) {
        return;
      }
      try {
        const res = await this.$axios({
          url: "user/agent/publish",
          method: "POST",
          data: { data: await this.$aesEncrypt({ id: a.id, isPublic: open }) },
        });
        if (res.code === 200) {
          ElMessage.success(open ? "已提交审核，请等待管理员通过" : "已取消发布");
          await this.loadMine();
          await this.load();
        } else {
          ElMessage.error(res.msg || "操作失败");
        }
      } catch (e) {
        ElMessage.error("操作失败");
      }
    },

    async removeAgent(a) {
      try {
        await ElMessageBox.confirm(
          `确定删除「${a.name}」？资源库里的资料也会一并清除。`,
          "删除智能体",
          { confirmButtonText: "删除", cancelButtonText: "取消", type: "warning" }
        );
      } catch (e) {
        return;
      }
      try {
        const res = await this.$axios({
          url: "user/agent/remove",
          method: "POST",
          data: { data: await this.$aesEncrypt({ id: a.id }) },
        });
        if (res.code === 200) {
          ElMessage.success("已删除");
          await this.loadMine();
          await this.load();
        }
      } catch (e) {
        ElMessage.error("删除失败");
      }
    },

    // ---------- 资源库 ----------
    async openResource(a) {
      this.resAgent = a;
      this.linkForm = { name: "", url: "" };
      this.resVisible = true;
      await this.loadResources();
    },

    async loadResources() {
      try {
        const res = await this.$axios({
          url: "user/agent/resource/list",
          method: "POST",
          data: { data: await this.$aesEncrypt({ id: this.resAgent.id }) },
        });
        if (res.code === 200 && res.data) {
          this.resources = res.data.list || [];
        }
      } catch (e) {
        this.resources = [];
      }
    },

    async uploadFile(ev) {
      const file = ev.target.files && ev.target.files[0];
      if (!file) return;
      if (file.size > 5 * 1024 * 1024) {
        ElMessage.warning("文件请控制在 5MB 以内");
        ev.target.value = "";
        return;
      }
      this.uploading = true;
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("agentId", this.resAgent.id);
        const res = await this.$axios({
          url: "user/agent/resource/upload",
          method: "POST",
          data: fd,
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (res.code === 200) {
          ElMessage.success(`已导入，切成 ${res.data.chunks} 段`);
          await this.loadResources();
          await this.loadMine();
        } else {
          ElMessage.error(res.msg || "导入失败");
        }
      } catch (e) {
        ElMessage.error("导入失败，请检查文件格式");
      }
      this.uploading = false;
      ev.target.value = "";
    },

    async addLink() {
      if (!this.linkForm.url.trim()) {
        ElMessage.warning("请填写链接地址");
        return;
      }
      try {
        const res = await this.$axios({
          url: "user/agent/resource/link",
          method: "POST",
          data: {
            data: await this.$aesEncrypt({
              agentId: this.resAgent.id,
              name: this.linkForm.name,
              url: this.linkForm.url,
            }),
          },
        });
        if (res.code === 200) {
          ElMessage.success("已添加");
          this.linkForm = { name: "", url: "" };
          await this.loadResources();
          await this.loadMine();
        } else {
          ElMessage.error(res.msg || "添加失败");
        }
      } catch (e) {
        ElMessage.error("添加失败");
      }
    },

    async removeResource(r) {
      try {
        await ElMessageBox.confirm(`确定删除「${r.name}」？`, "删除资源", {
          confirmButtonText: "删除",
          cancelButtonText: "取消",
          type: "warning",
        });
      } catch (e) {
        return;
      }
      try {
        const res = await this.$axios({
          url: "user/agent/resource/remove",
          method: "POST",
          data: { data: await this.$aesEncrypt({ id: r.id }) },
        });
        if (res.code === 200) {
          ElMessage.success("已删除");
          await this.loadResources();
          await this.loadMine();
        }
      } catch (e) {
        ElMessage.error("删除失败");
      }
    },

    // ---------- 使用 ----------
    openAgent(a) {
      this.activeAgent = a;
      if (a.mode === "embed") {
        if (!a.embedUrl) {
          ElMessage.warning("该智能体没有配置嵌入地址");
          return;
        }
        this.embedVisible = true;
        return;
      }
      if (a.mine) {
        if (!a.keyReady) {
          ElMessage.warning("请先在编辑里填写模型 API Key");
          return;
        }
      }
      this.messages = [];
      if (a.welcome) {
        this.messages.push({ role: "assistant", content: a.welcome });
      }
      this.guestRemaining = -1;
      this.chatVisible = true;
      this.$nextTick(this.scrollBottom);
    },

    scrollBottom() {
      const box = this.$refs.msgBox;
      if (box) box.scrollTop = box.scrollHeight;
    },

    async send() {
      const q = this.input.trim();
      if (!q || this.sending) return;
      this.input = "";
      this.sending = true;
      this.messages.push({ role: "user", content: q });
      this.messages.push({ role: "assistant", content: "", loading: true });
      const idx = this.messages.length - 1;
      this.$nextTick(this.scrollBottom);

      try {
        const payload = await this.$aesEncrypt({
          agentId: this.activeAgent.id,
          message: q,
        });
        await this.streamChat(payload, idx);
      } catch (e) {
        this.messages[idx].loading = false;
        this.messages[idx].content = "⚠️ " + (e.message || "调用失败，请稍后再试");
      }
      this.messages[idx].loading = false;
      this.sending = false;
      this.$nextTick(this.scrollBottom);
    },

    /** XHR 读流：fetch 的 reader 在部分浏览器会被缓冲到末尾 */
    streamChat(payload, idx) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open(
          "POST",
          process.env.VUE_APP_API_ROOT + "/user/agent/chat/stream"
        );
        xhr.setRequestHeader("Content-Type", "application/json;charset=UTF-8");
        const token = localStorage.getItem("token");
        if (token) xhr.setRequestHeader("Authorization", "Bearer " + token);

        let pos = 0;
        let buf = "";
        let started = false;

        const handle = (text) => {
          buf += text;
          const parts = buf.split("\n\n");
          buf = parts.pop() || "";
          for (const p of parts) {
            const ev = (p.match(/event:\s*(\S+)/) || [])[1] || "";
            const raw = (p.match(/data:\s*(.*)/) || [])[1] || "";
            if (!raw) continue;
            let val = raw;
            try {
              val = JSON.parse(raw);
            } catch (err) {
              /* 非 JSON 按原文处理 */
            }
            if (ev === "delta") {
              if (!started) {
                started = true;
                this.messages[idx].loading = false;
              }
              this.messages[idx].content += val;
              this.scrollBottom();
            } else if (ev === "done") {
              if (val !== "" && Number(val) >= 0) {
                this.guestRemaining = Number(val);
              }
            } else if (ev === "error") {
              reject(new Error(val));
              return;
            }
          }
        };

        xhr.onprogress = () => {
          const text = xhr.responseText || "";
          if (text.length > pos) {
            handle(text.slice(pos));
            pos = text.length;
          }
        };
        xhr.onload = () => {
          const text = xhr.responseText || "";
          if (text.length > pos) handle(text.slice(pos));
          resolve();
        };
        xhr.onerror = () => reject(new Error("网络异常"));
        xhr.send(JSON.stringify({ data: payload }));
      });
    },

    colorOf(id) {
      const palette = [
        "#6366f1",
        "#0ea5e9",
        "#10b981",
        "#f59e0b",
        "#ef4444",
        "#8b5cf6",
        "#14b8a6",
      ];
      let h = 0;
      const s = String(id || "");
      for (let i = 0; i < s.length; i++) {
        h = (h * 31 + s.charCodeAt(i)) % 9973;
      }
      return palette[h % palette.length];
    },
  },
};
</script>

<style scoped>
.knowledge-agent :deep(.line-clamp-2) {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
